import { AppRoutes } from "@/shared/lib/routes"
import CosmicBg from "@/assets/img/landing/cosmic-bg.webp"
import { useRouterState } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { usePlanSelection } from "@/features/products"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { LegalLink } from "@/shared/components/legal-link"
import { PlanCard } from "./plan-card"
import { PlanSummary } from "./plan-summary"
import { PaymentButtons } from "./payment-buttons"
import { MoneyBackGuarantee } from "./money-back-guarantee"
import { CardPaymentSheet } from "./card-payment-sheet"
import { ApplePaySheet } from "./apple-pay-sheet"
import { isApplePayAvailable, useCheckout } from "../hooks/use-checkout"
import { buildRenewalDisclaimer } from "../domain/disclaimer"
import { hasAdditionalOffers } from "@/features/offers"
import { isValidEmail } from "@/shared/lib/email"

type OpenSheet = "card" | "applepay" | null

/** The standard checkout layout — the `default` checkout variant. */
export function DefaultCheckout() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  // The default checkout is shared; the Simplified funnel's named Sub events
  // (Trello QZm294ZE) fire only when it's the active quiz. TODO: generalise if
  // other default-checkout funnels later need their own Sub events.
  const isSimplified = useQuizStore((s) => s.activeQuizId) === "simplified"
  const { visiblePlans, selectedPlan, setSelectedPlan, isLoading } = usePlanSelection()

  const [sheet, setSheet] = useState<OpenSheet>(null)
  const [applePayAvailable] = useState(isApplePayAvailable)
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false })

  // After purchase: the config-driven additional-offers funnel takes priority,
  // then the legacy single upsell, otherwise straight to the account step.
  const nextRoute = hasAdditionalOffers()
    ? AppRoutes.ADDITIONAL_OFFERS
    : typeof search.upsell === "string" && search.upsell
      ? AppRoutes.UPSELL
      : AppRoutes.CREATE_ACCOUNT

  useEffect(() => {
    if (!selectedPlan) return
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    })
    if (isSimplified) trackEvent(ANALYTICS_EVENTS.SIMPLIFIED_SUB_SCREEN_SHOWN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id])

  useEffect(() => {
    if (purchaseStatus === "success") {
      navigate({ to: nextRoute })
    }
  }, [purchaseStatus, navigate, nextRoute])

  const handleSuccess = () => {
    setSheet(null)
    navigate({ to: nextRoute })
  }

  // Apple Pay: when the quiz already captured a valid email there's nothing to
  // confirm, so launch the Apple Pay session straight from the click (keeping the
  // user gesture). Only fall back to the email-collection sheet when it's missing.
  const handleApplePay = async () => {
    if (isSimplified) {
      trackEvent(ANALYTICS_EVENTS.SIMPLIFIED_SUB_SCREEN_SUB_BUTTON_CLICK, {
        metadata: { method: "apple" },
      })
    }
    if (isValidEmail(email)) {
      const ok = await payWithApplePay()
      if (ok) handleSuccess()
    } else {
      setSheet("applepay")
    }
  }

  const singlePlan = visiblePlans.length === 1

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-dark-base font-body text-purple-light">
      <img
        src={CosmicBg}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 z-0 mx-auto w-full max-w-app [-webkit-mask-image:linear-gradient(to_bottom,#000_0%,#000_55%,transparent_100%)] [mask-image:linear-gradient(to_bottom,#000_0%,#000_55%,transparent_100%)]"
      />
      <MobileLayout className="relative z-10 flex min-h-[100dvh] flex-col py-6">
        {isLoading || !selectedPlan ? (
          <div className="flex flex-1 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <h1 className="text-center font-display text-2xl font-medium leading-tight text-purple-light">
              {singlePlan ? (
                <>
                  You're <span className="text-main-purple">on the last step</span> before receiving
                  your report
                </>
              ) : (
                <>
                  Choose a Plan to Reveal{" "}
                  <span className="text-main-purple">More Personal Insights</span>
                </>
              )}
            </h1>

            <div className="mt-6 space-y-3">
              {singlePlan ? (
                <PlanSummary plan={selectedPlan} />
              ) : (
                visiblePlans.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    selected={selectedPlan.id === plan.id}
                    onSelect={() => setSelectedPlan(plan)}
                  />
                ))
              )}
            </div>

            <div className="mt-6">
              <PaymentButtons
                applePayAvailable={applePayAvailable}
                onApplePay={handleApplePay}
                onCard={() => {
                  if (isSimplified) {
                    trackEvent(ANALYTICS_EVENTS.SIMPLIFIED_SUB_SCREEN_SUB_BUTTON_CLICK, {
                      metadata: { method: "card" },
                    })
                  }
                  setSheet("card")
                }}
              />
            </div>

            <div className="mt-auto pt-10">
              <MoneyBackGuarantee />

              <p className="mt-4 text-center text-xs leading-relaxed text-purple-light/60">
                {buildRenewalDisclaimer(selectedPlan)}
              </p>

              <div className="mt-5 h-px w-full bg-purple-light/10" />

              <footer className="flex items-center justify-center gap-4 pt-5 text-xs text-purple-light/60">
                <LegalLink to={AppRoutes.PRIVACY} className="hover:text-purple-light">
                  Privacy Policy
                </LegalLink>
                <LegalLink to={AppRoutes.TERMS} className="hover:text-purple-light">
                  Terms of use
                </LegalLink>
                <LegalLink to={AppRoutes.COOKIES} className="hover:text-purple-light">
                  Cookie Policy
                </LegalLink>
              </footer>
            </div>
          </>
        )}
      </MobileLayout>

      {selectedPlan && (
        <>
          <CardPaymentSheet
            plan={selectedPlan}
            email={email}
            open={sheet === "card"}
            onClose={() => setSheet(null)}
            onSuccess={handleSuccess}
          />
          <ApplePaySheet
            plan={selectedPlan}
            email={email}
            open={sheet === "applepay"}
            onClose={() => setSheet(null)}
            onSuccess={handleSuccess}
          />
        </>
      )}
    </div>
  )
}
